import * as Sentry from '@sentry/react';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { lazy, Suspense } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';
import { StaticFlags } from 'src/common/hooks/useStaticFlags/StaticFlagsHook.types';
import AppUpdateGuard from 'src/components/AppUpdateGuard';
import LoaderOverlay from 'src/components/Loader';
import ErrorFallback from 'src/routes/ErrorFallback';
import FlagProtectedRoute from 'src/routes/FlagProtectedRoute/FlagProtectedRoute';
import ProtectedRoute from 'src/routes/ProtectedRoute';
import { specializedStoresRoutes } from 'src/features/specializedStores/presentation/specializedStores.routes';
import { getRouteProtections } from 'src/features/specializedStores/domain/utils/getRouteProtections';
import ThreeDSRedirectScreen from 'src/features/orders/presentation/ThreeDSRedirectScreen';

const CartPage = lazy(() => import('src/features/cart/presentation/CartPage'));

const CheckoutPage = lazy(
  () => import('src/features/checkout/presentation/CheckoutPage')
);

const Openpay3dsStatus = lazy(
  () =>
    import(
      'src/features/checkout/presentation/components/Openpay3dsStatus/Openpay3dsStatus'
    )
);

const TagProducts = lazy(
  () => import('src/features/products/presentation/TagProducts')
);

const HomeScreen = lazy(
  () => import('src/features/home/presentation/HomeScreen')
);

const LoginPage = lazy(
  () => import('src/features/auth/presentation/LoginPage')
);
const PasswordResetPage = lazy(
  () => import('src/features/auth/presentation/PasswordResetPage')
);
const RegistrationPage = lazy(
  () => import('src/features/auth/presentation/RegistrationPage')
);
const AllTheProducts = lazy(
  () => import('src/features/products/presentation/AllTheProducts')
);
const CategoryProducts = lazy(
  () => import('src/features/products/presentation/CategoryProducts')
);
const CategoryPromotions = lazy(
  () => import('src/features/products/presentation/CategoryPromotions')
);
const ProductDetail = lazy(
  () => import('src/features/products/presentation/ProductDetail')
);
const CategoriesPage = lazy(
  () => import('src/features/categories/CategoriesPage')
);
const CreateAddressScreen = lazy(
  () => import('src/features/addresses/presentation/CreateAddressScreen')
);
const EditAddressScreen = lazy(
  () => import('src/features/addresses/presentation/EditAddressScreen')
);
const UserAddressesScreen = lazy(
  () => import('src/features/addresses/presentation/UserAddressesScreen')
);
const ProfileScreen = lazy(
  () => import('src/features/profile/presentation/ProfileScreen')
);
const EditProfileScreen = lazy(
  () => import('src/features/profile/presentation/EditProfileScreen')
);
const DeleteProfileScreen = lazy(
  () => import('src/features/profile/presentation/DeleteProfileScreen')
);
const OrderListScreen = lazy(
  () => import('src/features/orders/presentation/OrderListScreen')
);
const OrderDetailScreen = lazy(
  () => import('src/features/orders/presentation/OrderDetailScreen')
);
const PaymentMethodScreen = lazy(
  () => import('src/features/paymentMethods/presentation/PaymentMethodsScreen')
);
const NewCardScreen = lazy(
  () =>
    import('src/features/paymentMethods/presentation/components/NewCardScreen')
);
const PromotionsScreen = lazy(
  () => import('src/features/products/presentation/PromotionsScreen')
);
const ThreeDSChallengePage = lazy(
  () =>
    import('src/features/threeDSChallenge/presentation/ThreeDSChallengePage')
);
const WalletScreen = lazy(
  () => import('src/features/pariWallet/presentation/PariWalletScreen')
);
const WalletTransactionsScreen = lazy(
  () => import('src/features/pariWallet/presentation/WalletTransactionsScreen')
);

const ExternalCheckoutStatusHandler = lazy(
  () =>
    import(
      'src/features/paymentMethods/presentation/components/ExternalCheckoutStatusHandler'
    )
);

const UnauthorizedPage = lazy(
  () => import('src/features/unauthorized/presentation/UnauthorizedPage')
);

const BrandsScreen = lazy(
  () => import('src/features/brands/presentation/pages/BrandsScreen')
);

const BrandProducts = lazy(
  () => import('src/features/products/presentation/BrandProducts')
);

const NPSForm = lazy(
  () => import('src/features/nps/presentation/components/NPSForm')
);

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

export default function RoutesConfig() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <Suspense fallback={<LoaderOverlay />}>
        <SentryRoutes>
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route
            path="/nps/users/:user_uuid"
            element={
              <ProtectedRoute
                {...getRouteProtections('/nps/users/:user_uuid')}
                disableAuthProtection
              >
                <NPSForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute {...getRouteProtections('/')}>
                <HomeScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <ProtectedRoute {...getRouteProtections('/cart')}>
                <CartPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute {...getRouteProtections('/checkout')}>
                <AppUpdateGuard>
                  <CheckoutPage />
                </AppUpdateGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/3ds-response"
            element={
              <ProtectedRoute
                {...getRouteProtections('/checkout/3ds-response')}
              >
                <Openpay3dsStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/login"
            element={
              <ProtectedRoute
                {...getRouteProtections('/login')}
                expectedAuthState="non-authenticated"
              >
                <LoginPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/registration"
            element={
              <ProtectedRoute
                {...getRouteProtections('/registration')}
                expectedAuthState="non-authenticated"
              >
                <FlagProtectedRoute
                  static
                  expectedValue="on"
                  flag={StaticFlags.EnableSignUp}
                  redirectTo="/login"
                >
                  <RegistrationPage />
                </FlagProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/password-reset"
            element={
              <ProtectedRoute
                {...getRouteProtections('/password-reset')}
                disableAuthProtection
              >
                <PasswordResetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute {...getRouteProtections('/products')}>
                <AllTheProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:productSlug/:productId"
            element={
              <ProtectedRoute
                {...getRouteProtections('/products/:productSlug/:productId')}
              >
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute {...getRouteProtections('/categories')}>
                <CategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories/:categorySlug/products"
            element={
              <ProtectedRoute
                {...getRouteProtections('/categories/:categorySlug/products')}
              >
                <CategoryProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/highlighted/:tagSlug/products"
            element={
              <ProtectedRoute
                {...getRouteProtections('/highlighted/:tagSlug/products')}
              >
                <TagProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promotions/tag/:tagSlug"
            element={
              <ProtectedRoute
                {...getRouteProtections('/promotions/tag/:tagSlug')}
              >
                <TagProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promociones"
            element={
              <ProtectedRoute {...getRouteProtections('/promociones')}>
                <PromotionsScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promociones/:categorySlug/products"
            element={
              <ProtectedRoute
                {...getRouteProtections('/promociones/:categorySlug/products')}
              >
                <CategoryPromotions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/address/new"
            element={
              <ProtectedRoute {...getRouteProtections('/address/new')}>
                <CreateAddressScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/address/edit/:addressPk"
            element={
              <ProtectedRoute
                {...getRouteProtections('/address/edit/:addressPk')}
              >
                <EditAddressScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/addresslist"
            element={
              <ProtectedRoute {...getRouteProtections('/addresslist')}>
                <UserAddressesScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute {...getRouteProtections('/profile')}>
                <ProfileScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile-edit"
            element={
              <ProtectedRoute {...getRouteProtections('/profile-edit')}>
                <EditProfileScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/delete-account"
            element={
              <ProtectedRoute {...getRouteProtections('/delete-account')}>
                <DeleteProfileScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute {...getRouteProtections('/orders')}>
                <OrderListScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-detail/:orderId"
            element={
              <ProtectedRoute
                {...getRouteProtections('/order-detail/:orderId')}
              >
                <OrderDetailScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/3ds-verification/:orderId"
            element={
              <ProtectedRoute
                {...getRouteProtections('/3ds-verification/:orderId')}
              >
                <ThreeDSChallengePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-cards-list"
            element={
              <ProtectedRoute {...getRouteProtections('/payment-cards-list')}>
                <PaymentMethodScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cards/new"
            element={
              <ProtectedRoute {...getRouteProtections('/cards/new')}>
                <NewCardScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute {...getRouteProtections('/wallet')}>
                <WalletScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet/transactions"
            element={
              <ProtectedRoute {...getRouteProtections('/wallet/transactions')}>
                <WalletTransactionsScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-verification"
            element={
              <ProtectedRoute {...getRouteProtections('/payment-verification')}>
                <ExternalCheckoutStatusHandler />
              </ProtectedRoute>
            }
          />
          <Route path="/3ds-redirect" element={<ThreeDSRedirectScreen />} />
          <Route
            path="/brands"
            element={
              <ProtectedRoute {...getRouteProtections('/brands')}>
                <BrandsScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands/:brandSlug/products"
            element={
              <ProtectedRoute
                {...getRouteProtections('/brands/:brandSlug/products')}
              >
                <BrandProducts />
              </ProtectedRoute>
            }
          />
          <Route path="/specialized-stores" element={<Outlet />}>
            {specializedStoresRoutes}
          </Route>
          <Route
            path="*"
            element={
              <ProtectedRoute {...getRouteProtections('*')}>
                <UnauthorizedPage />
              </ProtectedRoute>
            }
          />
        </SentryRoutes>
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}
